import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isDesktop } from 'react-device-detect';
import {
  selectEmail,
  selectDeeplinkUrl,
  selectDeeplinkQR,
} from '../../store/signup';
import { APP_ONE_LINK } from '../../analytics/appsflyer';
import { EVENTS } from '../../analytics/events';
import { useSendEvents } from '../../hooks/useSendEvents';
import AppButton from '../../components/AppButton';
import * as S from './styled';

const Loader = ({ fill = '#00bfa5', ...rest }) => (
  <S.Loader
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...rest}
  >
    <path
      d="M48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24ZM6.38657 24C6.38657 33.7276 14.2724 41.6134 24 41.6134C33.7276 41.6134 41.6134 33.7276 41.6134 24C41.6134 14.2724 33.7276 6.38657 24 6.38657C14.2724 6.38657 6.38657 14.2724 6.38657 24Z"
      fillOpacity="0.5"
      fill={fill}
    />
    <path
      d="M24 3.19329C24 1.42968 25.436 -0.0218585 27.184 0.21211C31.2157 0.751748 35.0628 2.31025 38.3511 4.76341C42.4966 7.85606 45.5321 12.2051 47.0055 17.1628C48.4789 22.1205 48.3114 27.4215 46.5279 32.2762C45.1131 36.1271 42.7417 39.5338 39.6591 42.1878C38.3226 43.3384 36.3269 42.9068 35.3637 41.4294C34.4005 39.9521 34.843 37.9919 36.1224 36.7781C38.0786 34.9223 39.5934 32.6316 40.533 30.0739C41.8419 26.511 41.9649 22.6206 40.8836 18.9822C39.8022 15.3438 37.5745 12.1521 34.5322 9.88241C32.3481 8.25305 29.8282 7.16147 27.1759 6.67524C25.4412 6.35723 24 4.95689 24 3.19329Z"
      fill={fill}
    />
  </S.Loader>
);

const SuccessScreen = () => {
  const { t } = useTranslation();
  const screenId = 'success';
  const sendEvents = useSendEvents({ extraPathname: screenId });
  const email = useSelector(selectEmail);
  const deeplinkValue = useSelector(selectDeeplinkUrl);
  const deeplinkQR = useSelector(selectDeeplinkQR);
  const [initEventSent, setInitEventStatus] = useState(false);
  const [deepLink, setDeepLink] = useState('');
  const customStyles = `
    font-weight: 700;
    min-height: 48px;
    max-width: 434px;
    height: 56px;
    position: relative;
    margin-top: ${isDesktop ? '32px' : 'auto'};
    cursor: ${!deepLink ? 'not-allowed' : 'pointer'};
  `;
  const onContinueClick = () => {
    if (!deepLink) return;
    sendEvents(EVENTS.onboardingDone);
    window.location.href = deepLink;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDeepLink(APP_ONE_LINK);
    }, 7000);

    if (!deeplinkValue) return;
    clearTimeout(timeout);
    setDeepLink(deeplinkValue);
  }, [deeplinkValue]);

  useEffect(() => {
    if (initEventSent) return;
    sendEvents(EVENTS.onboardingSuccessShown);
    sendEvents(`${screenId}__screen__load`, { screen__load: screenId });
    setInitEventStatus(true);
  }, [sendEvents, initEventSent]);

  return (
    <S.SuccessWrapper>
      <S.TitleWrapper>
        <S.GeozillaIcon />
        <S.Title>{t('your_plans_awaits')}</S.Title>
        <S.Subtitle>
          {t(`download_app_via_${isDesktop ? 'qr' : 'button'}`)}
        </S.Subtitle>
      </S.TitleWrapper>
      <S.InstructionsTitle>{t('follow_these_steps')}</S.InstructionsTitle>
      <S.InstructionsWrapper>
        <S.ItemRow>
          <div dangerouslySetInnerHTML={{ __html: t('tap_on_login_screen') }} />
          <S.Screenshot>
            <S.ScreenshotButton>{t('continue')}</S.ScreenshotButton>
            <S.ScreenshotHighlight>
              {t('i_have_an_account')}
            </S.ScreenshotHighlight>
          </S.Screenshot>
        </S.ItemRow>
        <S.ItemRow
          dangerouslySetInnerHTML={{ __html: t('sign_in_with', { email }) }}
        />
      </S.InstructionsWrapper>
      <S.CheersTitle>{t('enjoy_using')}</S.CheersTitle>
      <S.CheersText>{t('dont_need_to_pay')}</S.CheersText>
      {isDesktop && (
        <S.DesktopQR>
          <S.QRCode $img={deeplinkQR}>
            {!deeplinkQR && <Loader style={{ width: 30, height: 30 }} />}
          </S.QRCode>
          {t('scan_qr_code')}
        </S.DesktopQR>
      )}
      <AppButton
        customId={'success-button'}
        onClick={onContinueClick}
        customStyles={customStyles}
      >
        {!deepLink ? <Loader fill={'#fff'} /> : t('download_app')}
      </AppButton>
      <input type="hidden" id="end-of-onboarding" />
    </S.SuccessWrapper>
  );
};

export default SuccessScreen;
